import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { asMoney } from "@bw/shared/types";
import { useEffect, useRef, useState } from "react";
import type { ReactNode, SubmitEvent } from "react";

import {
  updateTournament,
  type PricingModel,
  type RefundTier,
  type ScoringSystemConfig,
  type Tournament,
  type TournamentComplianceRequirementOverrides,
  type TournamentUpdate,
} from "@/libs/tournamentApi";
import { normalizePricingModel } from "@/libs/tournamentTypes";

type PricingTierForm = {
  event_count: string;
  fee: string;
};

type AirportForm = {
  code: string;
  name: string;
};

type RefundTierForm = {
  days_before: string;
  percent: string;
};

type ComplianceRequirementsForm = {
  [Key in keyof NonNullable<TournamentComplianceRequirementOverrides>]: boolean;
};

type ComplianceRequirementOverridesForm =
  NonNullable<TournamentComplianceRequirementOverrides>;

type FormState = {
  name: string;
  description: string;
  venue_name: string;
  venue_address: string;
  start_date: string;
  end_date: string;
  registration_open_at: string;
  registration_close_at: string;
  pricing_model: PricingModel;
  pricing_tiers: PricingTierForm[];
  refund_policy_summary: string;
  refund_deadline_at: string;
  refund_tiers: RefundTierForm[];
  refund_retained_fee: string;
  required_federation: string;
  compliance_requirements: ComplianceRequirementsForm;
  compliance_requirement_overrides: ComplianceRequirementOverridesForm;
  coach_pass_required: boolean;
  scoring_system: {
    type: string;
    games_per_match: string;
    points_per_game: string;
    setting_point: string;
  };
  tournament_logistics: {
    hotel: {
      name: string;
      address: string;
      booking_url: string;
      room_block_code: string;
    };
    airports: AirportForm[];
    parking: {
      notes: string;
      fee: string;
    };
  };
  organizer_email: string;
  organizer_phone: string;
  organizer_website: string;
  match_duration_minutes: string;
};

interface Props {
  open: boolean;
  tournament: Tournament;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

type SettingsMode = "simple" | "advanced";

const emptyScoring: ScoringSystemConfig = {
  type: "rally_point",
  games_per_match: 3,
  points_per_game: 21,
  setting_point: 29,
};

const ordinaryLocalRequirements: ComplianceRequirementsForm = {
  require_federation_membership: false,
  require_ncsi_for_18plus: false,
  require_foreign_country_declaration: false,
};

const toDateInput = (iso: string | null | undefined) =>
  iso ? iso.slice(0, 10) : "";
const toIso = (date: string) => (date ? date + "T00:00:00Z" : null);
const moneyToInput = (value: string | number | null | undefined) =>
  value === null || value === undefined ? "" : String(value);
const optionalString = (value: string) => {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const parsePositiveInt = (value: string, label: string) => {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) {
    throw new Error(label + " must be a positive whole number.");
  }
  return parsed;
};

const parseNonNegativeInt = (value: string, label: string) => {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 0) {
    throw new Error(label + " must be zero or a positive whole number.");
  }
  return parsed;
};

const buildForm = (tournament: Tournament): FormState => {
  const scoring = tournament.scoring_system ?? emptyScoring;
  const logistics = tournament.tournament_logistics;

  return {
    name: tournament.name,
    description: tournament.description ?? "",
    venue_name: tournament.venue_name ?? "",
    venue_address: tournament.venue_address ?? "",
    start_date: toDateInput(tournament.start_date),
    end_date: toDateInput(tournament.end_date),
    registration_open_at: toDateInput(tournament.registration_open_at),
    registration_close_at: toDateInput(tournament.registration_close_at),
    pricing_model: normalizePricingModel(tournament.pricing_model),
    pricing_tiers: (tournament.pricing_tiers ?? []).map((tier) => ({
      event_count: String(tier.event_count),
      fee: moneyToInput(tier.fee),
    })),
    refund_policy_summary: tournament.refund_policy_summary ?? "",
    refund_deadline_at: toDateInput(tournament.refund_deadline_at),
    refund_tiers: (tournament.refund_tiers ?? []).flatMap((tier) =>
      typeof tier.days_before === "number" && typeof tier.percent === "number"
        ? [
            {
              days_before: String(tier.days_before),
              percent: String(tier.percent),
            },
          ]
        : [],
    ),
    refund_retained_fee: (() => {
      const retained = (tournament.refund_tiers ?? []).find(
        (tier) => tier.retained_fee !== undefined,
      )?.retained_fee;
      return retained === undefined || retained === null
        ? ""
        : String(retained);
    })(),
    required_federation: tournament.required_federation ?? "",
    compliance_requirements: {
      ...ordinaryLocalRequirements,
      ...tournament.compliance_requirements,
    },
    compliance_requirement_overrides: {},
    coach_pass_required: Boolean(tournament.coach_pass_required),
    scoring_system: {
      type: scoring.type,
      games_per_match: String(scoring.games_per_match),
      points_per_game: String(scoring.points_per_game),
      setting_point: String(scoring.setting_point),
    },
    tournament_logistics: {
      hotel: {
        name: logistics?.hotel?.name ?? "",
        address: logistics?.hotel?.address ?? "",
        booking_url: logistics?.hotel?.booking_url ?? "",
        room_block_code: logistics?.hotel?.room_block_code ?? "",
      },
      airports: (logistics?.airports ?? []).map((airport) => ({
        code: airport.code,
        name: airport.name ?? "",
      })),
      parking: {
        notes: logistics?.parking?.notes ?? "",
        fee: moneyToInput(logistics?.parking?.fee),
      },
    },
    organizer_email: tournament.organizer_email ?? "",
    organizer_phone: tournament.organizer_phone ?? "",
    organizer_website: tournament.organizer_website ?? "",
    match_duration_minutes:
      tournament.match_duration_minutes === null ||
      tournament.match_duration_minutes === undefined
        ? ""
        : String(tournament.match_duration_minutes),
  };
};

const makePayload = (form: FormState): TournamentUpdate => {
  if (!form.name.trim()) throw new Error("Name is required.");

  const pricingTiers: NonNullable<TournamentUpdate["pricing_tiers"]> | null =
    form.pricing_model === "tiered_volume"
      ? form.pricing_tiers.map((tier) => ({
          event_count: parsePositiveInt(
            tier.event_count,
            "Pricing event count",
          ),
          fee: asMoney(tier.fee.trim())!,
        }))
      : null;

  if (pricingTiers !== null && pricingTiers.length === 0) {
    throw new Error("Add at least one pricing tier.");
  }
  if (pricingTiers !== null && pricingTiers.some((tier) => tier.fee === "")) {
    throw new Error("Pricing tier fee is required.");
  }

  const refundTiers: RefundTier[] = form.refund_tiers.map((tier, index) => {
    const percent = Number(tier.percent);
    if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
      throw new Error(
        "Refund tier " + String(index + 1) + " must be between 0% and 100%.",
      );
    }
    return {
      days_before: parseNonNegativeInt(
        tier.days_before,
        "Refund tier days before start",
      ),
      percent,
    };
  });
  const retainedFee = asMoney(optionalString(form.refund_retained_fee));
  if (retainedFee !== null) {
    refundTiers.push({ retained_fee: retainedFee });
  }

  const rawScoringType = form.scoring_system.type.trim() || "rally_point";
  let scoringType: "rally_point" | "traditional";
  if (rawScoringType === "rally_point" || rawScoringType === "traditional") {
    scoringType = rawScoringType;
  } else {
    throw new Error(
      "Scoring type must be Rally scoring or traditional scoring.",
    );
  }
  const gamesPerMatch = parsePositiveInt(
    form.scoring_system.games_per_match,
    "Games per match",
  );
  if (![1, 3, 5].includes(gamesPerMatch)) {
    throw new Error("Games per match must be 1, 3, or 5.");
  }
  const pointsPerGame = parsePositiveInt(
    form.scoring_system.points_per_game,
    "Points per game",
  );
  if (pointsPerGame > 30) {
    throw new Error("Points per game cannot exceed 30.");
  }
  const settingPoint = parsePositiveInt(
    form.scoring_system.setting_point,
    "Setting point",
  );
  if (settingPoint > 29) {
    throw new Error("Setting point cannot exceed 29.");
  }
  if (settingPoint < pointsPerGame) {
    throw new Error("Setting point must be at least points per game.");
  }
  const scoring_system: ScoringSystemConfig = {
    type: scoringType,
    games_per_match: gamesPerMatch as 1 | 3 | 5,
    points_per_game: pointsPerGame,
    setting_point: settingPoint,
  };

  const hotel = form.tournament_logistics.hotel;
  const parking = form.tournament_logistics.parking;
  const airports = form.tournament_logistics.airports
    .map((airport) => ({
      code: airport.code.trim().toUpperCase(),
      name: optionalString(airport.name),
    }))
    .filter((airport) => airport.code || airport.name);

  if (airports.some((airport) => airport.code.length !== 3)) {
    throw new Error("Airport code must be exactly three letters.");
  }

  const tournament_logistics: NonNullable<
    TournamentUpdate["tournament_logistics"]
  > = {
    hotel: {
      name: optionalString(hotel.name),
      address: optionalString(hotel.address),
      booking_url: optionalString(hotel.booking_url),
      room_block_code: optionalString(hotel.room_block_code),
    },
    airports,
    parking: {
      notes: optionalString(parking.notes),
      fee: asMoney(optionalString(parking.fee)),
    },
  };

  const complianceOverrides = form.compliance_requirement_overrides;

  return {
    name: form.name.trim(),
    description: optionalString(form.description),
    venue_name: optionalString(form.venue_name),
    venue_address: optionalString(form.venue_address),
    start_date: toIso(form.start_date),
    end_date: toIso(form.end_date),
    registration_open_at: toIso(form.registration_open_at),
    registration_close_at: toIso(form.registration_close_at),
    pricing_model: form.pricing_model,
    pricing_tiers: pricingTiers,
    refund_policy_summary: optionalString(form.refund_policy_summary),
    refund_deadline_at: toIso(form.refund_deadline_at),
    refund_tiers: refundTiers,
    required_federation: optionalString(form.required_federation),
    ...(Object.keys(complianceOverrides).length > 0
      ? { compliance_requirements: complianceOverrides }
      : {}),
    coach_pass_required: form.coach_pass_required,
    scoring_system,
    tournament_logistics,
    organizer_email: optionalString(form.organizer_email),
    organizer_phone: optionalString(form.organizer_phone),
    organizer_website: optionalString(form.organizer_website),
    match_duration_minutes: form.match_duration_minutes.trim()
      ? parsePositiveInt(form.match_duration_minutes, "Match duration")
      : null,
  };
};

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <Box component="section">
      <Typography component="h3" sx={{ fontWeight: 700 }} variant="subtitle1">
        {title}
      </Typography>
      {description ? (
        <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
          {description}
        </Typography>
      ) : (
        <Box sx={{ mb: 2 }} />
      )}
      {children}
    </Box>
  );
}

const gridSx = {
  display: "grid",
  gap: 2,
  gridTemplateColumns: {
    xs: "minmax(0, 1fr)",
    sm: "repeat(2, minmax(0, 1fr))",
  },
};

const fieldProps = {
  fullWidth: true,
  size: "small" as const,
};

const dateFieldProps = {
  ...fieldProps,
  slotProps: { inputLabel: { shrink: true } },
  type: "date",
};

export function TournamentSettingsDialog({
  open,
  tournament,
  onClose,
  onSaved,
}: Props) {
  const fullScreen = useMediaQuery("(max-width:599.95px)");
  const mountedRef = useRef(true);
  const sessionGenerationRef = useRef(0);
  const inFlightSessionRef = useRef<number | null>(null);
  // The state initializer already owns the first open render. Avoid an
  // immediately-following effect reset: a person can start typing as soon as
  // the dialog becomes visible, and that reset used to overwrite part of the
  // value mid-edit. Later re-opens or a different tournament still rebuild
  // from the authoritative props below.
  const initializedTournamentIdRef = useRef<string | null>(
    open ? tournament.tournament_id : null,
  );
  const [form, setForm] = useState<FormState>(() => buildForm(tournament));
  const [settingsMode, setSettingsMode] = useState<SettingsMode>("simple");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      sessionGenerationRef.current += 1;
      inFlightSessionRef.current = null;
    };
  }, []);

  useEffect(() => {
    sessionGenerationRef.current += 1;
    inFlightSessionRef.current = null;
    setSaving(false);
    if (!open) {
      initializedTournamentIdRef.current = null;
      return;
    }
    if (initializedTournamentIdRef.current === tournament.tournament_id) {
      return;
    }
    initializedTournamentIdRef.current = tournament.tournament_id;
    setForm(buildForm(tournament));
    setSettingsMode("simple");
    setError(null);
  }, [open, tournament.tournament_id]);

  const set = <Key extends keyof FormState>(key: Key, value: FormState[Key]) =>
    setForm((previous) => ({ ...previous, [key]: value }));

  const setComplianceRequirement = (
    key: keyof ComplianceRequirementsForm,
    value: boolean,
  ) => {
    setForm((previous) => ({
      ...previous,
      compliance_requirements: {
        ...previous.compliance_requirements,
        [key]: value,
      },
      compliance_requirement_overrides: {
        ...previous.compliance_requirement_overrides,
        [key]: value,
      },
    }));
  };

  const requestClose = () => {
    if (inFlightSessionRef.current !== null) return;
    onClose();
  };

  const save = async () => {
    if (inFlightSessionRef.current !== null) return;

    let payload: TournamentUpdate;
    try {
      payload = makePayload(form);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Save failed");
      return;
    }

    const sessionGeneration = sessionGenerationRef.current;
    const tournamentId = tournament.tournament_id;
    inFlightSessionRef.current = sessionGeneration;
    setError(null);
    setSaving(true);
    try {
      await updateTournament(tournamentId, payload);
      if (
        !mountedRef.current ||
        sessionGenerationRef.current !== sessionGeneration
      ) {
        return;
      }
      await onSaved();
      if (
        mountedRef.current &&
        sessionGenerationRef.current === sessionGeneration
      ) {
        onClose();
      }
    } catch (cause) {
      if (
        mountedRef.current &&
        sessionGenerationRef.current === sessionGeneration
      ) {
        setError(cause instanceof Error ? cause.message : "Save failed");
      }
    } finally {
      if (inFlightSessionRef.current === sessionGeneration) {
        inFlightSessionRef.current = null;
      }
      if (
        mountedRef.current &&
        sessionGenerationRef.current === sessionGeneration
      ) {
        setSaving(false);
      }
    }
  };

  return (
    <Dialog
      aria-describedby="tournament-settings-description"
      aria-labelledby="tournament-settings-title"
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      onClose={requestClose}
      open={open}
      slotProps={{
        paper: {
          component: "form",
          onSubmit: (event: SubmitEvent<HTMLDivElement>) => {
            event.preventDefault();
            void save();
          },
        },
      }}
    >
      <DialogTitle id="tournament-settings-title">
        Tournament settings
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <DialogContentText id="tournament-settings-description">
            Manage the tournament schedule, entry policies, scoring, contacts,
            and travel information.
          </DialogContentText>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <Paper
            variant="outlined"
            sx={{
              bgcolor: "action.hover",
              borderColor: "divider",
              p: { xs: 2, sm: 2.5 },
            }}
          >
            <Stack spacing={1.5}>
              <Box>
                <Typography sx={{ fontWeight: 800 }}>
                  How much do you want to configure?
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  Start with the essentials. Advanced setup adds sanctioning,
                  custom match rules, pricing schedules, and travel details.
                  Switching views never discards your work.
                </Typography>
              </Box>
              <ToggleButtonGroup
                aria-label="Tournament setup detail"
                exclusive
                fullWidth
                onChange={(_, value: SettingsMode | null) => {
                  if (value) setSettingsMode(value);
                }}
                size="small"
                value={settingsMode}
              >
                <ToggleButton value="simple">Simple setup</ToggleButton>
                <ToggleButton value="advanced">Advanced setup</ToggleButton>
              </ToggleButtonGroup>
              {settingsMode === "simple" ? (
                <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
                  <Chip label="Dates and registration" size="small" />
                  <Chip label="Entry pricing" size="small" />
                  <Chip label="Refund summary" size="small" />
                  <Chip label="Organizer contact" size="small" />
                </Stack>
              ) : null}
            </Stack>
          </Paper>

          <Section
            title="Tournament details"
            description="The public name, dates, and venue participants see."
          >
            <Box sx={gridSx}>
              <TextField
                {...fieldProps}
                autoFocus
                label="Name"
                onChange={(event) => set("name", event.target.value)}
                sx={{ gridColumn: "1 / -1" }}
                value={form.name}
              />
              <TextField
                {...dateFieldProps}
                label="Start date"
                onChange={(event) => set("start_date", event.target.value)}
                value={form.start_date}
              />
              <TextField
                {...dateFieldProps}
                label="End date"
                onChange={(event) => set("end_date", event.target.value)}
                value={form.end_date}
              />
              <TextField
                {...dateFieldProps}
                label="Registration opens"
                onChange={(event) =>
                  set("registration_open_at", event.target.value)
                }
                value={form.registration_open_at}
              />
              <TextField
                {...dateFieldProps}
                label="Registration closes"
                onChange={(event) =>
                  set("registration_close_at", event.target.value)
                }
                value={form.registration_close_at}
              />
              <TextField
                {...fieldProps}
                label="Description"
                minRows={3}
                multiline
                onChange={(event) => set("description", event.target.value)}
                sx={{ gridColumn: "1 / -1" }}
                value={form.description}
              />
              {settingsMode === "advanced" ? (
                <>
                  <TextField
                    {...fieldProps}
                    label="Alternate venue name"
                    onChange={(event) => set("venue_name", event.target.value)}
                    placeholder="e.g. Independence High School"
                    value={form.venue_name}
                  />
                  <TextField
                    {...fieldProps}
                    helperText="Leave blank to use the facility address."
                    label="Alternate venue address"
                    minRows={2}
                    multiline
                    onChange={(event) =>
                      set("venue_address", event.target.value)
                    }
                    value={form.venue_address}
                  />
                </>
              ) : null}
            </Box>
          </Section>

          <Divider />

          <Section
            title="Pricing and refunds"
            description="Configure entry fees and the policy shown to registrants."
          >
            <Stack spacing={2}>
              <TextField
                {...fieldProps}
                label="Pricing model"
                onChange={(event) => {
                  const value = event.target.value as PricingModel;
                  set("pricing_model", value);
                  if (value === "tiered_volume") setSettingsMode("advanced");
                }}
                select
                value={form.pricing_model}
              >
                <MenuItem value="per_event">
                  Price each event separately
                </MenuItem>
                <MenuItem value="tiered_volume">
                  Multi-event package pricing
                </MenuItem>
              </TextField>

              {settingsMode === "advanced" &&
              form.pricing_model === "tiered_volume" ? (
                <Stack spacing={1.5}>
                  {form.pricing_tiers.map((tier, index) => (
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      key={String(index)}
                      spacing={1}
                      sx={{ alignItems: { sm: "center" } }}
                    >
                      <TextField
                        {...fieldProps}
                        label={"Tier " + String(index + 1) + " event count"}
                        onChange={(event) =>
                          set(
                            "pricing_tiers",
                            form.pricing_tiers.map((row, rowIndex) =>
                              rowIndex === index
                                ? {
                                    ...row,
                                    event_count: event.target.value,
                                  }
                                : row,
                            ),
                          )
                        }
                        slotProps={{
                          htmlInput: {
                            "aria-label":
                              "pricing_tiers " +
                              String(index + 1) +
                              " event_count",
                            inputMode: "numeric",
                          },
                        }}
                        value={tier.event_count}
                      />
                      <TextField
                        {...fieldProps}
                        label={"Tier " + String(index + 1) + " fee"}
                        onChange={(event) =>
                          set(
                            "pricing_tiers",
                            form.pricing_tiers.map((row, rowIndex) =>
                              rowIndex === index
                                ? { ...row, fee: event.target.value }
                                : row,
                            ),
                          )
                        }
                        slotProps={{
                          htmlInput: {
                            "aria-label":
                              "pricing_tiers " + String(index + 1) + " fee",
                            inputMode: "decimal",
                          },
                        }}
                        value={tier.fee}
                      />
                      <Tooltip title="Remove pricing tier">
                        <IconButton
                          aria-label="Remove pricing tier"
                          onClick={() =>
                            set(
                              "pricing_tiers",
                              form.pricing_tiers.filter(
                                (_, rowIndex) => rowIndex !== index,
                              ),
                            )
                          }
                          type="button"
                        >
                          <DeleteOutlineRoundedIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ))}
                  <Button
                    onClick={() =>
                      set("pricing_tiers", [
                        ...form.pricing_tiers,
                        { event_count: "", fee: "" },
                      ])
                    }
                    startIcon={<AddRoundedIcon />}
                    sx={{ alignSelf: "flex-start" }}
                    type="button"
                    variant="outlined"
                  >
                    Add pricing tier
                  </Button>
                </Stack>
              ) : null}

              <Box sx={gridSx}>
                <TextField
                  {...fieldProps}
                  label="Refund policy summary"
                  minRows={2}
                  multiline
                  onChange={(event) =>
                    set("refund_policy_summary", event.target.value)
                  }
                  value={form.refund_policy_summary}
                />
                <TextField
                  {...dateFieldProps}
                  label="Refund deadline"
                  onChange={(event) =>
                    set("refund_deadline_at", event.target.value)
                  }
                  value={form.refund_deadline_at}
                />
              </Box>

              {settingsMode === "advanced" ? (
                <Stack spacing={1.5}>
                  <Box>
                    <Typography sx={{ fontWeight: 700 }} variant="subtitle2">
                      Automated refund schedule
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                      Set the refund percentage by how early a player withdraws.
                    </Typography>
                  </Box>
                  {form.refund_tiers.map((tier, index) => (
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      key={String(index)}
                      spacing={1}
                      sx={{ alignItems: { sm: "center" } }}
                    >
                      <TextField
                        {...fieldProps}
                        label="Days before tournament"
                        onChange={(event) =>
                          set(
                            "refund_tiers",
                            form.refund_tiers.map((row, rowIndex) =>
                              rowIndex === index
                                ? { ...row, days_before: event.target.value }
                                : row,
                            ),
                          )
                        }
                        slotProps={{ htmlInput: { inputMode: "numeric" } }}
                        value={tier.days_before}
                      />
                      <TextField
                        {...fieldProps}
                        label="Refund percentage"
                        onChange={(event) =>
                          set(
                            "refund_tiers",
                            form.refund_tiers.map((row, rowIndex) =>
                              rowIndex === index
                                ? { ...row, percent: event.target.value }
                                : row,
                            ),
                          )
                        }
                        slotProps={{
                          htmlInput: { inputMode: "decimal", max: 100, min: 0 },
                        }}
                        value={tier.percent}
                      />
                      <Tooltip title="Remove refund tier">
                        <IconButton
                          aria-label="Remove refund tier"
                          onClick={() =>
                            set(
                              "refund_tiers",
                              form.refund_tiers.filter(
                                (_, rowIndex) => rowIndex !== index,
                              ),
                            )
                          }
                          type="button"
                        >
                          <DeleteOutlineRoundedIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ))}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <Button
                      onClick={() =>
                        set("refund_tiers", [
                          ...form.refund_tiers,
                          { days_before: "", percent: "" },
                        ])
                      }
                      startIcon={<AddRoundedIcon />}
                      sx={{ alignSelf: "flex-start" }}
                      type="button"
                      variant="outlined"
                    >
                      Add refund window
                    </Button>
                    <TextField
                      {...fieldProps}
                      helperText="Optional non-refundable processing amount."
                      label="Retained processing fee"
                      onChange={(event) =>
                        set("refund_retained_fee", event.target.value)
                      }
                      slotProps={{ htmlInput: { inputMode: "decimal" } }}
                      value={form.refund_retained_fee}
                    />
                  </Stack>
                </Stack>
              ) : null}
            </Stack>
          </Section>

          {settingsMode === "advanced" ? <Divider /> : null}

          {settingsMode === "advanced" ? (
            <Section
              title="Eligibility and scoring"
              description="Set federation requirements and the default match format."
            >
              <Stack spacing={2}>
                <Box sx={gridSx}>
                  <TextField
                    {...fieldProps}
                    helperText="For example, BWF or USA Badminton."
                    label="Governing organization"
                    onChange={(event) =>
                      set("required_federation", event.target.value)
                    }
                    value={form.required_federation}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.coach_pass_required}
                        onChange={(event) =>
                          set("coach_pass_required", event.target.checked)
                        }
                      />
                    }
                    label="Coach pass required"
                    sx={{ m: 0 }}
                  />
                </Box>
                <Typography variant="subtitle2">Entry requirements</Typography>
                <Typography color="text.secondary" variant="body2">
                  Apply only the requirements that the tournament's governing
                  body requires.
                </Typography>
                <Box sx={gridSx}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={
                          form.compliance_requirements
                            .require_federation_membership
                        }
                        onChange={(event) =>
                          setComplianceRequirement(
                            "require_federation_membership",
                            event.target.checked,
                          )
                        }
                      />
                    }
                    label="Require federation membership"
                    sx={{ m: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={
                          form.compliance_requirements.require_ncsi_for_18plus
                        }
                        onChange={(event) =>
                          setComplianceRequirement(
                            "require_ncsi_for_18plus",
                            event.target.checked,
                          )
                        }
                      />
                    }
                    label="Require adult participant background check"
                    sx={{ m: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={
                          form.compliance_requirements
                            .require_foreign_country_declaration
                        }
                        onChange={(event) =>
                          setComplianceRequirement(
                            "require_foreign_country_declaration",
                            event.target.checked,
                          )
                        }
                      />
                    }
                    label="Require international representation declaration"
                    sx={{ m: 0 }}
                  />
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: {
                      xs: "minmax(0, 1fr)",
                      sm: "repeat(2, minmax(0, 1fr))",
                      md: "repeat(4, minmax(0, 1fr))",
                    },
                  }}
                >
                  <TextField
                    {...fieldProps}
                    label="Scoring type"
                    select
                    onChange={(event) =>
                      set("scoring_system", {
                        ...form.scoring_system,
                        type: event.target.value,
                      })
                    }
                    value={form.scoring_system.type}
                  >
                    <MenuItem value="rally_point">Rally scoring</MenuItem>
                    <MenuItem value="traditional">
                      Service scoring (legacy)
                    </MenuItem>
                  </TextField>
                  <TextField
                    {...fieldProps}
                    label="Games per match"
                    onChange={(event) =>
                      set("scoring_system", {
                        ...form.scoring_system,
                        games_per_match: event.target.value,
                      })
                    }
                    slotProps={{
                      htmlInput: { inputMode: "numeric" },
                    }}
                    value={form.scoring_system.games_per_match}
                  />
                  <TextField
                    {...fieldProps}
                    label="Points per game"
                    onChange={(event) =>
                      set("scoring_system", {
                        ...form.scoring_system,
                        points_per_game: event.target.value,
                      })
                    }
                    slotProps={{
                      htmlInput: { inputMode: "numeric" },
                    }}
                    value={form.scoring_system.points_per_game}
                  />
                  <TextField
                    {...fieldProps}
                    helperText="At 29–29, the next point wins."
                    label="Deuce cap threshold"
                    onChange={(event) =>
                      set("scoring_system", {
                        ...form.scoring_system,
                        setting_point: event.target.value,
                      })
                    }
                    slotProps={{
                      htmlInput: { inputMode: "numeric" },
                    }}
                    value={form.scoring_system.setting_point}
                  />
                </Box>
              </Stack>
            </Section>
          ) : null}

          <Divider />

          <Section
            title="Contacts and logistics"
            description="Operational contacts, expected match time, travel, and parking."
          >
            <Stack spacing={2}>
              <Box sx={gridSx}>
                <TextField
                  {...fieldProps}
                  label="Organizer email"
                  onChange={(event) =>
                    set("organizer_email", event.target.value)
                  }
                  value={form.organizer_email}
                />
                {settingsMode === "advanced" ? (
                  <TextField
                    {...fieldProps}
                    label="Organizer phone"
                    onChange={(event) =>
                      set("organizer_phone", event.target.value)
                    }
                    value={form.organizer_phone}
                  />
                ) : null}
                {settingsMode === "advanced" ? (
                  <TextField
                    {...fieldProps}
                    label="Organizer website"
                    onChange={(event) =>
                      set("organizer_website", event.target.value)
                    }
                    value={form.organizer_website}
                  />
                ) : null}
                <TextField
                  {...fieldProps}
                  label="Match duration (minutes)"
                  onChange={(event) =>
                    set("match_duration_minutes", event.target.value)
                  }
                  slotProps={{
                    htmlInput: { inputMode: "numeric" },
                  }}
                  value={form.match_duration_minutes}
                />
              </Box>

              {settingsMode === "advanced" ? (
                <>
                  <Typography component="h4" variant="subtitle2">
                    Hotel
                  </Typography>
                  <Box sx={gridSx}>
                    <TextField
                      {...fieldProps}
                      label="Hotel name"
                      onChange={(event) =>
                        set("tournament_logistics", {
                          ...form.tournament_logistics,
                          hotel: {
                            ...form.tournament_logistics.hotel,
                            name: event.target.value,
                          },
                        })
                      }
                      value={form.tournament_logistics.hotel.name}
                    />
                    <TextField
                      {...fieldProps}
                      label="Hotel address"
                      onChange={(event) =>
                        set("tournament_logistics", {
                          ...form.tournament_logistics,
                          hotel: {
                            ...form.tournament_logistics.hotel,
                            address: event.target.value,
                          },
                        })
                      }
                      value={form.tournament_logistics.hotel.address}
                    />
                    <TextField
                      {...fieldProps}
                      label="Hotel booking link"
                      onChange={(event) =>
                        set("tournament_logistics", {
                          ...form.tournament_logistics,
                          hotel: {
                            ...form.tournament_logistics.hotel,
                            booking_url: event.target.value,
                          },
                        })
                      }
                      value={form.tournament_logistics.hotel.booking_url}
                    />
                    <TextField
                      {...fieldProps}
                      label="Room block code"
                      onChange={(event) =>
                        set("tournament_logistics", {
                          ...form.tournament_logistics,
                          hotel: {
                            ...form.tournament_logistics.hotel,
                            room_block_code: event.target.value,
                          },
                        })
                      }
                      value={form.tournament_logistics.hotel.room_block_code}
                    />
                  </Box>

                  <Typography component="h4" variant="subtitle2">
                    Airports
                  </Typography>
                  {form.tournament_logistics.airports.map((airport, index) => (
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      key={String(index)}
                      spacing={1}
                      sx={{ alignItems: { sm: "center" } }}
                    >
                      <TextField
                        {...fieldProps}
                        label={"Airport " + String(index + 1) + " code"}
                        onChange={(event) =>
                          set("tournament_logistics", {
                            ...form.tournament_logistics,
                            airports: form.tournament_logistics.airports.map(
                              (row, rowIndex) =>
                                rowIndex === index
                                  ? { ...row, code: event.target.value }
                                  : row,
                            ),
                          })
                        }
                        slotProps={{
                          htmlInput: {
                            "aria-label":
                              "airports " + String(index + 1) + " code",
                            maxLength: 3,
                          },
                        }}
                        sx={{ maxWidth: { sm: 180 } }}
                        value={airport.code}
                      />
                      <TextField
                        {...fieldProps}
                        label={"Airport " + String(index + 1) + " name"}
                        onChange={(event) =>
                          set("tournament_logistics", {
                            ...form.tournament_logistics,
                            airports: form.tournament_logistics.airports.map(
                              (row, rowIndex) =>
                                rowIndex === index
                                  ? { ...row, name: event.target.value }
                                  : row,
                            ),
                          })
                        }
                        slotProps={{
                          htmlInput: {
                            "aria-label":
                              "airports " + String(index + 1) + " name",
                          },
                        }}
                        value={airport.name}
                      />
                      <Tooltip title="Remove airport">
                        <IconButton
                          aria-label="Remove airport"
                          onClick={() =>
                            set("tournament_logistics", {
                              ...form.tournament_logistics,
                              airports:
                                form.tournament_logistics.airports.filter(
                                  (_, rowIndex) => rowIndex !== index,
                                ),
                            })
                          }
                          type="button"
                        >
                          <DeleteOutlineRoundedIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ))}
                  <Button
                    onClick={() =>
                      set("tournament_logistics", {
                        ...form.tournament_logistics,
                        airports: [
                          ...form.tournament_logistics.airports,
                          { code: "", name: "" },
                        ],
                      })
                    }
                    startIcon={<AddRoundedIcon />}
                    sx={{ alignSelf: "flex-start" }}
                    type="button"
                    variant="outlined"
                  >
                    Add airport
                  </Button>

                  <Box sx={gridSx}>
                    <TextField
                      {...fieldProps}
                      label="Parking notes"
                      onChange={(event) =>
                        set("tournament_logistics", {
                          ...form.tournament_logistics,
                          parking: {
                            ...form.tournament_logistics.parking,
                            notes: event.target.value,
                          },
                        })
                      }
                      value={form.tournament_logistics.parking.notes}
                    />
                    <TextField
                      {...fieldProps}
                      label="Parking fee"
                      onChange={(event) =>
                        set("tournament_logistics", {
                          ...form.tournament_logistics,
                          parking: {
                            ...form.tournament_logistics.parking,
                            fee: event.target.value,
                          },
                        })
                      }
                      slotProps={{
                        htmlInput: { inputMode: "decimal" },
                      }}
                      value={form.tournament_logistics.parking.fee}
                    />
                  </Box>
                </>
              ) : null}
            </Stack>
          </Section>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={saving} onClick={requestClose} variant="outlined">
          Cancel
        </Button>
        <Button disabled={saving} type="submit" variant="contained">
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
